/**
 * @fileoverview Admin Projects Page Component
 *
 * Displays all projects with search, filtering, and card/list view toggle.
 * Primary admin interface for project management and navigation to project details.
 *
 * Features:
 * - Search by project name, address, or client
 * - Filter by status, construction phase, and project type
 * - Toggle between card and list views
 * - Click-through to individual project detail pages
 * - Mobile-optimized for field use
 *
 * @requires react
 * @requires react-router-dom
 * @requires react-icons/fa
 * @version 1.0.0
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouseChimneyUser,
  faHouseFlag,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import {
  FaSearch,
  FaFilter,
  FaTh,
  FaList,
  FaChevronDown,
  FaChevronUp,
  FaHome,
  FaSwimmingPool,
  FaMapMarkerAlt,
  FaUser,
  FaDollarSign,
  FaCalendarAlt,
  FaSpinner,
  FaExclamationTriangle,
  FaFolderOpen,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { formatDateET } from "../../utils/dateUtils";
import { formatProgress } from "../../utils/projectUtils";
import {
  useAdminProjects,
  useCreateProject,
} from "../../hooks/useAdminProjects";
import useUserPreferences from "../../hooks/useUserPreferences";
import ProjectModal from "../../components/admin/ProjectModal";
import AdminPageBanner from "../../components/admin/AdminPageBanner";
import "./AdminProjects.css";

/* ============================================
   CONSTANTS
   ============================================ */

/**
 * Project status options for filtering
 * Note: "Warranty Period Complete" and "Job Lost" are excluded from the list
 * as they are filtered out at the API level
 * @constant {Array<Object>}
 */
const PROJECT_STATUSES = [
  { value: "all", label: "All Active" },
  { value: "Pre-Construction", label: "Pre-Construction" },
  { value: "Active Construction", label: "Active Construction" },
  { value: "Construction Delayed", label: "Construction Delayed" },
  {
    value: "Construction Complete / Under Warranty",
    label: "Under Warranty",
  },
];

/**
 * Construction phase options for filtering
 * @constant {Array<Object>}
 */
const CONSTRUCTION_PHASES = [
  { value: "all", label: "All Phases" },
  { value: "In Development", label: "In Development" },
  { value: "Planning/Permitting", label: "Planning/Permitting" },
  { value: "Foundation", label: "Foundation" },
  { value: "Framing", label: "Framing" },
  { value: "Rough-In", label: "Rough-In" },
  { value: "Insulation & Drywall", label: "Insulation & Drywall" },
  { value: "Trim & Paint", label: "Trim & Paint" },
  { value: "Mechanical Trim", label: "Mechanical Trim" },
  { value: "Final Finishes", label: "Final Finishes" },
  { value: "Punch List", label: "Punch List" },
  { value: "Final Inspection / CO", label: "Final Inspection" },
  { value: "Complete", label: "Complete" },
];

/**
 * Project type options for filtering
 * @constant {Array<Object>}
 */
const PROJECT_TYPES = [
  { value: "all", label: "All Types" },
  { value: "Custom Home", label: "Custom Home" },
  { value: "Spec House", label: "Spec House" },
  { value: "Commercial", label: "Commercial" },
  { value: "Pool", label: "Pool" },
  { value: "Internal", label: "Internal" },
];

/**
 * Maps project type to corresponding icon
 * @param {string} type - Project type
 * @returns {JSX.Element} Icon component
 */
export const getProjectTypeIcon = (type) => {
  switch (type) {
    case "Custom Home":
      return <FontAwesomeIcon icon={faHouseChimneyUser} />;
    case "Spec House":
      return <FontAwesomeIcon icon={faHouseFlag} />;
    case "Commercial":
      return <FontAwesomeIcon icon={faBuilding} />;
    case "Pool":
      return <FaSwimmingPool />;
    default:
      return <FaHome />;
  }
};

/**
 * Returns project type color class for card header
 * @param {string} type - Project type
 * @returns {string} CSS class name
 */
const getProjectTypeColor = (type) => {
  switch (type) {
    case "Custom Home":
      return "admin-projects-type-custom";
    case "Spec House":
      return "admin-projects-type-spec";
    case "Commercial":
      return "admin-projects-type-commercial";
    case "Pool":
      return "admin-projects-type-pool";
    default:
      return "admin-projects-type-default";
  }
};

/**
 * Returns project type icon class for list view
 * @param {string} type - Project type
 * @returns {string} CSS class name
 */
export const getProjectTypeIconClass = (type) => {
  switch (type) {
    case "Custom Home":
      return "type-custom";
    case "Spec House":
      return "type-spec";
    case "Commercial":
      return "type-commercial";
    case "Pool":
      return "type-pool";
    default:
      return "type-default";
  }
};

/**
 * Returns status badge color class
 * @param {string} status - Project status
 * @returns {string} CSS class name
 */
export const getStatusLabel = (status) =>
  status === "Construction Complete / Under Warranty"
    ? "Under Warranty"
    : status;

export const getStatusColor = (status) => {
  switch (status) {
    case "Active Construction":
      return "admin-projects-status-active";
    case "Pre-Construction":
      return "admin-projects-status-pre";
    case "Construction Delayed":
      return "admin-projects-status-delayed";
    case "Construction Complete / Under Warranty":
      return "admin-projects-status-warranty";
    case "Warranty Period Complete":
      return "admin-projects-status-complete";
    case "Job Lost":
      return "admin-projects-status-lost";
    default:
      return "admin-projects-status-default";
  }
};

/* ============================================
   SUB-COMPONENTS
   ============================================ */

/**
 * Search and filter controls bar
 * @param {Object} props - Component props
 */
const SearchFilterBar = ({
  searchTerm,
  onSearchChange,
  showFilters,
  onToggleFilters,
  viewMode,
  onViewModeChange,
  activeFilterCount,
  onAddClick,
}) => (
  <div className="admin-projects-controls">
    {/* Search Input */}
    <div className="admin-projects-search">
      <FaSearch className="admin-projects-search-icon" />
      <input
        type="text"
        placeholder="Search projects, addresses, or clients..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="admin-projects-search-input"
      />
    </div>

    {/* Control Buttons */}
    <div className="admin-projects-control-buttons">
      {/* Filter Toggle */}
      <button
        className={`admin-projects-filter-btn ${showFilters ? "active" : ""}`}
        onClick={onToggleFilters}
      >
        <FaFilter />
        <span>Filters</span>
        {activeFilterCount > 0 && (
          <span className="admin-projects-filter-count">
            {activeFilterCount}
          </span>
        )}
        {showFilters ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      {/* View Mode Toggle */}
      <div className="admin-projects-view-toggle">
        <button
          className={`admin-projects-view-btn ${
            viewMode === "card" ? "active" : ""
          }`}
          onClick={() => onViewModeChange("card")}
          data-tooltip="Card View"
        >
          <FaTh /> <span className="hide-sm">Card View</span>
        </button>
        <button
          className={`admin-projects-view-btn ${
            viewMode === "list" ? "active" : ""
          }`}
          onClick={() => onViewModeChange("list")}
          data-tooltip="List View"
        >
          <FaList /> <span className="hide-sm">List View</span>
        </button>
      </div>
    </div>
  </div>
);

/**
 * Collapsible filter panel
 * @param {Object} props - Component props
 */
const FilterPanel = ({
  filters,
  onFilterChange,
  onClearFilters,
  isVisible,
}) => {
  if (!isVisible) return null;

  return (
    <div className="admin-projects-filters">
      <div className="admin-projects-filter-row">
        {/* Status Filter */}
        <div className="admin-projects-filter-group">
          <label className="admin-projects-filter-label">Status</label>
          <select
            value={filters.status}
            onChange={(e) => onFilterChange("status", e.target.value)}
            className="admin-projects-filter-select"
          >
            {PROJECT_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {/* Phase Filter */}
        <div className="admin-projects-filter-group">
          <label className="admin-projects-filter-label">Phase</label>
          <select
            value={filters.phase}
            onChange={(e) => onFilterChange("phase", e.target.value)}
            className="admin-projects-filter-select"
          >
            {CONSTRUCTION_PHASES.map((phase) => (
              <option key={phase.value} value={phase.value}>
                {phase.label}
              </option>
            ))}
          </select>
        </div>

        {/* Type Filter */}
        <div className="admin-projects-filter-group">
          <label className="admin-projects-filter-label">Type</label>
          <select
            value={filters.type}
            onChange={(e) => onFilterChange("type", e.target.value)}
            className="admin-projects-filter-select"
          >
            {PROJECT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        <button
          className="admin-projects-clear-filters"
          onClick={onClearFilters}
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

/**
 * Project card for grid view
 * @param {Object} props - Component props
 */
const ProjectCard = ({ project, onClick }) => (
  <div className="admin-projects-card" onClick={() => onClick(project.id)}>
    {/* Card Header - Color coded by project type */}
    <div
      className={`admin-projects-card-header ${getProjectTypeColor(
        project.projectType,
      )}`}
    >
      <div className="admin-projects-card-type">
        {getProjectTypeIcon(project.projectType)}
        <span>{project.projectType}</span>
      </div>
      <span
        className={`admin-projects-card-status ${getStatusColor(
          project.projectStatus,
        )}`}
      >
        {getStatusLabel(project.projectStatus)}
      </span>
    </div>

    {/* Card Body */}
    <div className="admin-projects-card-body">
      <h3 className="admin-projects-card-title">{project.projectName}</h3>

      <div className="admin-projects-card-info">
        <div className="admin-projects-card-row">
          <FaMapMarkerAlt className="admin-projects-card-icon" />
          <span className="admin-projects-card-address">
            {project.projectAddress}
          </span>
        </div>

        <div className="admin-projects-card-row">
          <FaUser className="admin-projects-card-icon" />
          <span>{project.clientName || "─"}</span>
        </div>

        <div className="admin-projects-card-row">
          <FaDollarSign className="admin-projects-card-icon" />
          <span>
            {project.budget
              ? `$${project.budget.toLocaleString()}`
              : "Budget TBD"}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="admin-projects-card-progress">
        <div className="admin-projects-progress-header">
          <span className="admin-projects-progress-phase">
            {project.constructionPhase}
          </span>
          <span className="admin-projects-progress-percent">
            {formatProgress(project.projectProgress)}%
          </span>
        </div>
        <div className="admin-projects-progress-bar">
          <div
            className="admin-projects-progress-fill"
            style={{ width: `${formatProgress(project.projectProgress)}%` }}
          />
        </div>
      </div>
    </div>

    {/* Card Footer */}
    <div className="admin-projects-card-footer">
      <div className="admin-projects-card-date">
        <FaCalendarAlt />
        <span>Start: {formatDateET(project.startDate)}</span>
      </div>
      <div className="admin-projects-card-date">
        <FaCalendarAlt />
        {project.projectStatus ===
        "Construction Complete / Under Warranty" ? (
          <span>
            Completed:{" "}
            {project.actualCompletion
              ? formatDateET(project.actualCompletion)
              : "—"}
          </span>
        ) : (
          <span>
            Est. Complete: {formatDateET(project.estimatedCompletion)}
          </span>
        )}
      </div>
    </div>
  </div>
);

/**
 * Project row for list view
 * @param {Object} props - Component props
 */
export const ProjectRow = ({ project, onClick, showCompletedDate = false }) => (
  <tr className="admin-projects-row" onClick={() => onClick(project.id)}>
    <td className="admin-projects-cell-name">
      <div className="admin-projects-name-cell">
        <span
          className={`admin-projects-type-icon ${getProjectTypeIconClass(
            project.projectType,
          )}`}
        >
          {getProjectTypeIcon(project.projectType)}
        </span>
        <div>
          <span className="admin-projects-row-title">
            {project.projectName}
          </span>
          <span className="admin-projects-row-address">
            {project.projectAddress}
          </span>
        </div>
      </div>
    </td>
    <td className="admin-projects-cell hide-md">{project.clientName || "—"}</td>
    <td className="admin-projects-cell hide-md">
      {showCompletedDate
        ? project.actualCompletion
          ? formatDateET(project.actualCompletion)
          : "—"
        : project.startDate
          ? formatDateET(project.startDate)
          : "—"}
    </td>
    <td className="admin-projects-cell">
      <span
        className={`admin-projects-status-badge ${getStatusColor(
          project.projectStatus,
        )}`}
      >
        {getStatusLabel(project.projectStatus)}
      </span>
    </td>
    <td className="admin-projects-cell hide-lg">{project.constructionPhase}</td>
    <td className="admin-projects-cell">
      <div className="admin-projects-row-progress">
        <div className="admin-projects-row-progress-bar">
          <div
            className="admin-projects-row-progress-fill"
            style={{ width: `${formatProgress(project.projectProgress)}%` }}
          />
        </div>
        <span>{formatProgress(project.projectProgress)}%</span>
      </div>
    </td>
  </tr>
);

/**
 * Mobile accordion item for list view
 * Expandable row showing full project details
 * @param {Object} props - Component props
 */
export const MobileAccordionItem = ({
  project,
  isExpanded,
  onToggle,
  onNavigate,
}) => (
  <div className="admin-projects-accordion-item">
    {/* Accordion Header - Always visible */}
    <div className="admin-projects-accordion-header" onClick={onToggle}>
      <span
        className={`admin-projects-type-icon ${getProjectTypeIconClass(
          project.projectType,
        )}`}
      >
        {getProjectTypeIcon(project.projectType)}
      </span>
      <div className="admin-projects-accordion-title">
        <span className="admin-projects-accordion-name">
          {project.projectName}
        </span>
        <span className="admin-projects-accordion-address">
          {project.projectAddress}
        </span>
      </div>
      <span
        className={`admin-projects-accordion-chevron ${
          isExpanded ? "expanded" : ""
        }`}
      >
        <FaChevronDown />
      </span>
    </div>

    {/* Accordion Content - Shown when expanded */}
    {isExpanded && (
      <div className="admin-projects-accordion-content">
        {/* Go to Project Button */}
        <button
          className="admin-projects-accordion-goto"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(project.id);
          }}
        >
          <span>Go to Project</span>
          <FaExternalLinkAlt />
        </button>

        {/* Status & Type Row */}
        <div className="admin-projects-accordion-row">
          <span
            className={`admin-projects-card-status ${getStatusColor(
              project.projectStatus,
            )}`}
          >
            {getStatusLabel(project.projectStatus)}
          </span>
          <span className="admin-projects-accordion-type">
            {project.projectType}
          </span>
        </div>

        {/* Details */}
        <div className="admin-projects-accordion-details">
          <div className="admin-projects-accordion-detail">
            <FaUser className="admin-projects-accordion-icon" />
            <span>{project.clientName || "No client assigned"}</span>
          </div>

          <div className="admin-projects-accordion-detail">
            <FaDollarSign className="admin-projects-accordion-icon" />
            <span>
              {project.budget
                ? `$${project.budget.toLocaleString()}`
                : "Budget TBD"}
            </span>
          </div>

          <div className="admin-projects-accordion-detail">
            <FaCalendarAlt className="admin-projects-accordion-icon" />
            <span>Start: {formatDateET(project.startDate)}</span>
          </div>

          <div className="admin-projects-accordion-detail">
            <FaCalendarAlt className="admin-projects-accordion-icon" />
            {project.projectStatus ===
            "Construction Complete / Under Warranty" ? (
              <span>
                Completed:{" "}
                {project.actualCompletion
                  ? formatDateET(project.actualCompletion)
                  : "—"}
              </span>
            ) : (
              <span>
                Est. Complete: {formatDateET(project.estimatedCompletion)}
              </span>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="admin-projects-accordion-progress">
          <div className="admin-projects-progress-header">
            <span className="admin-projects-progress-phase">
              {project.constructionPhase}
            </span>
            <span className="admin-projects-progress-percent">
              {formatProgress(project.projectProgress)}%
            </span>
          </div>
          <div className="admin-projects-progress-bar">
            <div
              className="admin-projects-progress-fill"
              style={{ width: `${formatProgress(project.projectProgress)}%` }}
            />
          </div>
        </div>
      </div>
    )}
  </div>
);

/* ============================================
   MAIN COMPONENT
   ============================================ */

/**
 * AdminProjects Component
 *
 * Main projects list page for admin portal.
 * Provides search, filtering, and navigation to project details.
 *
 * @param {Object} props - Component props
 * @param {Object} props.userData - Authenticated user data
 * @returns {JSX.Element} Admin projects page
 */
const AdminProjects = ({ userData, showTestAccounts }) => {
  const navigate = useNavigate();

  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("card");
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedAccordionId, setExpandedAccordionId] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    phase: "all",
    type: "all",
  });

  /**
   * Apply the signed-in user's saved Projects defaults (view mode
   * and sort) from Firestore once preferences have loaded. The
   * `preferencesLoaded` ref ensures later re-renders of the hook
   * do not overwrite in-session changes the user has made to the
   * view toggle or sort controls.
   */
  const { preferences, isLoading: preferencesLoading } = useUserPreferences();
  const preferencesLoaded = useRef(false);

  useEffect(() => {
    if (preferencesLoaded.current) return;
    if (preferencesLoading) return;
    setViewMode(preferences.projectsDefaultView);
    setSortBy(preferences.projectsDefaultSort);
    setSortDirection(preferences.projectsSortDirection);
    preferencesLoaded.current = true;
  }, [
    preferencesLoading,
    preferences.projectsDefaultView,
    preferences.projectsDefaultSort,
    preferences.projectsSortDirection,
  ]);

  // Determine if warranty status should be included
  // (when "Under Warranty" is specifically selected)
  const includeWarranty =
    filters.status === "Construction Complete / Under Warranty";

  // Fetch projects from Airtable via Firebase
  const {
    data: projects,
    isLoading,
    error,
  } = useAdminProjects({
    status: filters.status,
    phase: filters.phase,
    type: filters.type,
    showTestAccounts,
    includeWarranty,
  });

  // Project Modal state
  const [showProjectModal, setShowProjectModal] = useState(false);
  const createProjectMutation = useCreateProject();

  /**
   * Handle filter change
   * @param {string} filterKey - Filter key to update
   * @param {string} value - New filter value
   */
  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({
      status: "all",
      phase: "all",
      type: "all",
    });
  };

  /**
   * Navigate to project detail page
   * @param {string} projectId - Project record ID
   */
  const handleProjectClick = (projectId) => {
    navigate(`/admin/projects/${projectId}`);
  };

  /**
   * Count active filters
   * @returns {number} Number of active filters
   */
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter((v) => v !== "all").length;
  }, [filters]);

  /**
   * Filter, search, and sort projects
   * Default sort: by start date (newest first)
   */
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    const filtered = projects.filter((project) => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        project.projectName?.toLowerCase().includes(searchLower) ||
        project.projectAddress?.toLowerCase().includes(searchLower) ||
        project.clientName?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus =
        filters.status === "all" || project.projectStatus === filters.status;

      // Phase filter
      const matchesPhase =
        filters.phase === "all" || project.constructionPhase === filters.phase;

      // Type filter
      const matchesType =
        filters.type === "all" || project.projectType === filters.type;

      return matchesSearch && matchesStatus && matchesPhase && matchesType;
    });

    // Sort by user-selected column + direction. Falls back to
    // alphabetical on project name when no explicit sort applies.
    // Phases sort in the canonical field order defined by
    // CONSTRUCTION_PHASES, not alphabetically. Unknown / missing
    // phases sort to the end.
    const phaseOrder = CONSTRUCTION_PHASES.filter((p) => p.value !== "all").map(
      (p) => p.value,
    );
    const phaseIndex = (phase) => {
      const idx = phaseOrder.indexOf(phase);
      return idx === -1 ? phaseOrder.length : idx;
    };

    const sortKeyMap = {
      name: (p) => (p.projectName || "").toLowerCase(),
      client: (p) => {
        const parts = (p.clientName || "").trim().split(/\s+/);
        return (parts[parts.length - 1] || "").toLowerCase();
      },
      startDate: (p) =>
        includeWarranty
          ? p.actualCompletion
            ? new Date(p.actualCompletion).getTime()
            : 0
          : p.startDate
            ? new Date(p.startDate).getTime()
            : 0,
      status: (p) => (p.projectStatus || "").toLowerCase(),
      phase: (p) => phaseIndex(p.constructionPhase),
      progress: (p) => Number(p.projectProgress) || 0,
    };
    const getKey = sortKeyMap[sortBy] || sortKeyMap.startDate;
    const dir = sortDirection === "desc" ? -1 : 1;

    return [...filtered].sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
  }, [projects, searchTerm, filters, sortBy, sortDirection, includeWarranty]);

  /**
   * Toggle sort for a given column key. First click on a new
   * column sorts ascending; clicking the active column again
   * flips between ascending and descending.
   * @param {string} key - Column sort key
   */
  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="loading-container">
        <FaSpinner className="admin-dash-spinner" />
        <span className="loading-text">Loading projects...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="admin-projects-page">
        <div className="state-container">
          <FaExclamationTriangle className="state-icon" />
          <h2 className="state-title">Error Loading Projects</h2>
          <p className="state-message">
            {error.message || "Unable to load projects. Please try again."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-projects-page">
      {/* Page Banner */}
      <AdminPageBanner
        title="Projects"
        subtitle="Manage all active and upcoming projects"
        count={filteredProjects.length}
        countLabel="Active Projects"
        addButton={{
          label: "New Project",
          onClick: () => setShowProjectModal(true),
        }}
      />

      {/* Search and Filter Controls */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        activeFilterCount={activeFilterCount}
        onAddClick={() => setShowProjectModal(true)}
      />
      {/* Project Modal */}
      {showProjectModal && (
        <ProjectModal
          isOpen={showProjectModal}
          onClose={() => setShowProjectModal(false)}
          mode="full"
          mutations={{ createProject: createProjectMutation }}
        />
      )}
      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        isVisible={showFilters}
      />
      {/* Projects Display */}
      {filteredProjects.length === 0 ? (
        <div className="state-container">
          <FaFolderOpen className="state-icon" />
          <h2 className="state-title">No Projects Found</h2>
          <p className="state-message">
            {searchTerm || activeFilterCount > 0
              ? "Try adjusting your search or filters."
              : "No projects have been created yet."}
          </p>
        </div>
      ) : viewMode === "card" ? (
        /* Card View */
        <div className="admin-projects-grid">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={handleProjectClick}
            />
          ))}
        </div>
      ) : (
        /* List View */
        <>
          {/* Desktop/Tablet Table View */}
          <div className="admin-projects-table-container hide-mobile">
            <table className="admin-projects-table">
              <thead>
                <tr>
                  {[
                    { key: "name", label: "Project", className: "" },
                    { key: "client", label: "Client", className: "hide-md" },
                    {
                      key: "startDate",
                      label: includeWarranty ? "Completed Date" : "Start Date",
                      className: "hide-md",
                    },
                    { key: "status", label: "Status", className: "" },
                    { key: "phase", label: "Phase", className: "hide-lg" },
                    { key: "progress", label: "Progress", className: "" },
                  ].map((col) => {
                    const isActive = sortBy === col.key;
                    const classes = [
                      "admin-projects-sortable-th",
                      col.className,
                      isActive ? "active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <th
                        key={col.key}
                        className={classes}
                        onClick={() => handleSort(col.key)}
                      >
                        <span className="admin-projects-sortable-th-inner">
                          <span>{col.label}</span>
                          {isActive && (
                            <span className="admin-projects-sort-chevron">
                              {sortDirection === "asc" ? (
                                <FaChevronDown />
                              ) : (
                                <FaChevronUp />
                              )}
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    onClick={handleProjectClick}
                    showCompletedDate={includeWarranty}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Accordion View */}
          <div className="admin-projects-accordion show-mobile">
            {filteredProjects.map((project) => (
              <MobileAccordionItem
                key={project.id}
                project={project}
                isExpanded={expandedAccordionId === project.id}
                onToggle={() =>
                  setExpandedAccordionId(
                    expandedAccordionId === project.id ? null : project.id,
                  )
                }
                onNavigate={handleProjectClick}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminProjects;
